import type { ReactNode } from "react";
import { useState } from "react";
import type { InputProps } from "ui/forms/Input";
import type { InputRef } from "ui/forms/Input";
import Input from "ui/forms/Input";
import InputGroup from "ui/forms/InputGroup";
import Textarea from "ui/forms/Textarea";

export interface InputGroupTextProps extends Pick<InputProps, "validate" | "autoComplete" | "min" | "max"> {
  type: "textarea" | InputProps["type"];
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyPress?: (evt: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onCommit?: () => void;
  inputValue: string | string[] | null | undefined;
  labelText?: ReactNode;
  id?: string;
  name?: string;
  labelLink?: ReactNode;
  placeholder?: string | null;
  maxLength?: number;
  disabled?: boolean;
  required?: boolean;
  title?: string;
  inputSize?: "medium" | "large";
  tooltip?: ReactNode;
  className?: string;
  controlledInputState?: "error" | "success";
  multiple?: boolean;
  ref?: React.Ref<InputRef>;
  dataTestId?: string;
}

export default function InputGroupText({
  placeholder,
  labelText,
  labelLink,
  onChange,
  onBlur,
  onFocus,
  onKeyPress,
  onCommit,
  inputValue,
  autoComplete,
  maxLength,
  type,
  name,
  disabled,
  required,
  validate,
  title,
  inputSize,
  min,
  max,
  tooltip,
  className,
  id,
  controlledInputState,
  multiple,
  ref,
  dataTestId,
}: InputGroupTextProps) {
  const [inputState, setInputState] = useState<"error" | "success" | undefined>(undefined);

  const handleTextChange = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    try {
      const value = e.currentTarget.value;
      if (onChange) {
        onChange(value);
      }
    } catch (error) {
      setInputState("error");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    try {
      onKeyPress && onKeyPress(e);
    } catch (error) {
      setInputState("error");
    }
  };

  const computedInputState = controlledInputState ?? inputState;

  const getInputByType = () => {
    switch (type) {
      case "textarea": {
        return (
          <Textarea
            onChange={handleTextChange}
            onKeyPress={handleKeyPress}
            maxLength={maxLength}
            value={inputValue ?? ""}
            name={name}
            placeholder={placeholder !== null ? placeholder : undefined}
            disabled={disabled}
            inputState={computedInputState}
            title={title}
            required={required}
            validate={validate ? () => validate() ?? null : undefined}
            onFocus={onFocus}
            id={id}
            data-testid={dataTestId}
          />
        );
      }

      default: {
        return (
          <Input
            ref={ref}
            onCommit={onCommit}
            onBlur={onBlur}
            onChange={handleTextChange}
            onKeyPress={handleKeyPress}
            maxLength={maxLength}
            value={inputValue ?? ""}
            type={type}
            name={name}
            autoComplete={autoComplete}
            placeholder={placeholder ?? `Enter ${labelText}`}
            disabled={disabled}
            inputState={computedInputState}
            required={required}
            validate={validate}
            title={title}
            inputSize={inputSize}
            min={min}
            max={max}
            onFocus={onFocus}
            id={id}
            data-testid={dataTestId}
            multiple={multiple}
          />
        );
      }
    }
  };

  return (
    <InputGroup
      labelText={labelText}
      labelLink={labelLink}
      tooltip={tooltip}
      className={className}
      id={id}
      required={required}
    >
      {getInputByType()}
    </InputGroup>
  );
}
